import { Grid } from '@material-ui/core'
import styled from 'styled-components'
import { ITheme } from 'theme/interface'
import { ImageViewerContainerProps, ImageZoomProps } from './interfaces'

interface IMenuList {
  className?: string
  theme: ITheme
}

export const Container = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #fff;
`

export const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`

export const LeftContainer = styled.div<{
  windowHeight: number
  windowWidth: number
  isCenter?: boolean
}>`
  width: ${({ windowWidth, isCenter }) =>
    !isCenter ? '1200px' : `${windowWidth - 300}px`};
  margin: 0 auto;
  height: ${({ windowHeight }) => windowHeight}px;
  display: flex;
  overflow-y: scroll;

  @media (max-width: 480px) {
    width: 100%;
    height: 100%;
    overflow-y: hidden;
  }
`

export const RightContainer = styled.div`
  width: 300px;
  display: flex;
  padding-top: 60px;

  @media (max-width: 480px) {
    width: 100%;
  }
`

export const GridContent = styled(Grid)<{
  noMaxWidth?: boolean
  windowHeight?: number
  resize?: boolean
}>`
  width: 100%;
  height: 100%;
  position: relative;
  padding-top: 70px;
  overflow-y: scroll;
`

export const TopContent = styled.div`
  width: 100%;
  padding: 20px;

  .text-content {
    p,
    span {
      font-size: 14px !important;
      font-weight: 500 !important;
      color: ${({ theme }) => theme.colors.dark} !important;
    }

    p {
      font-weight: normal !important;
    }
  }

  .button-content {
    width: 100%;
    margin-top: 20px;
  }
`

export const Menu = styled.nav`
  margin-top: 20px;

  ul {
    width: 100%;

    li {
      width: 100% !important;
      list-style: none;

      button {
        width: 100%;
        background-color: transparent;
        height: 40px;
        border: none;
        text-align: left;
        font-size: 16px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.secondary};
        padding-left: 20px;
        outline: none !important;

        :disabled {
          opacity: 0.5;
          cursor: no-drop;
        }
      }
    }
  }
`

export const MenuList = styled.li<IMenuList>`
  width: 100%;
  background-color: ${({ className, theme }) =>
    className === 'active' ? theme.colors.background : 'transparent'};
  border-left: ${({ className, theme }) =>
    className === 'active'
      ? `2px solid ${theme.colors.secondary}`
      : 'none'} !important;

  border-top: 1px solid
    ${({ className, theme }) =>
      className === 'active' ? 'transparent' : theme.colors.gray10} !important;
  height: 40px;
  border: none;
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
`

export const PatientContent = styled.div`
  h3 {
    font-size: 18px !important;
    font-weight: 500 !important;
    color: ${({ theme }) => theme.colors.dark};
  }

  p {
    font-size: 14px !important;
    line-height: 20px !important;
    font-weight: normal !important;
    color: ${({ theme }) => theme.colors.gray80} !important;
    margin-top: 15px;

    span {
      font-weight: 600 !important;
    }
  }

  .PatientContent--header {
    @media screen and (min-width: 546px) {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-direction: row;
    }
  }
`

export const SectionContent = styled.section`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.gray10};
  border-radius: ${({ theme }) => theme.radius.default}px;
  padding: 30px;

  span {
    font-size: 16px !important;
    font-weight: 600 !important;
    line-height: 20px !important;
  }

  p {
    font-size: 14px !important;
    line-height: 18px !important;
    color: ${({ theme }) => theme.colors.gray50} !important;
  }

  .subsection {
    .subsection-item {
      display: flex;
      flex-direction: column;
      margin-bottom: 30px;

      span {
        font-size: 14px;
        color: ${({ theme }) => theme.colors.dark};
      }

      p {
        font-size: 14px !important;
        color: ${({ theme }) => theme.colors.dark} !important;
        font-weight: 600 !important;
        line-height: 22px !important;
      }

      .vital-content {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;

        .vital-content-left {
          width: 80%;
        }

        .vital-content-right {
          display: flex;
          flex: 1;
          justify-content: flex-start;
          padding-left: 10px;
        }

        .tag-container {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 15px;
          margin-bottom: 15px;
        }

        .tag-button {
          border-radius: 50px;
          padding: 5px 10px;
          font-size: 16px;
        }
      }
    }

    .cards-prescriptions {
      width: 100%;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
    }
  }
`

export const AttachmentsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: 40px;

  .files {
    width: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-right: 20px;
    border: none;
    outline: none;
    background-color: transparent;
    padding-top: 30px;

    img {
      width: 56px;
      height: 75.6px;
      margin-bottom: 10px;
    }

    span {
      max-width: 100px;
    }
  }

  .images {
    width: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-right: 20px;
    border: none;
    outline: none;
    background-color: transparent;

    img {
      width: 100%;
      margin-bottom: 10px;
    }

    span {
      max-width: 200px;
    }
  }
`

export const NoData = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  img {
    width: 200px;
  }

  span {
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.gray80};
    margin-top: 30px;
  }
`

export const CardPrescriptionExams = styled.div`
  width: 100%;
  min-height: 100px;
  margin-bottom: 20px;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.gray10};
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    width: 150px;
  }

  span {
    color: ${({ theme }) => theme.colors.gray80};
    margin-bottom: 10px;
  }

  @media screen and (min-width: 961px) {
    width: calc(100% / 3 - 10px);
    margin-right: 5px;
    margin-left: 5px;
  }
`

export const cardNutritional = styled.div`
  > h2 {
    > strong {
      font-size: 16px !important;
      font-weight: 700 !important;
    }
  }
  > p {
    font-size: 14px !important;
    margin-bottom: 0;
    font-weight: 500 !important;
    > strong {
      font-size: 14px !important;
      color: ${({ theme }) => theme.colors.gray80};
      font-weight: 700 !important;
    }
  }

  > ul {
    margin: 15px;
  }

  pre {
    padding: 20px;
  }
`

export const ContentPagination = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: flex-end;
  flex-direction: column;
`

export const MessageContainer = styled.div`
  width: 100%;
  min-height: 50px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.secondaryLight};
  margin-bottom: 15px;
  border-radius: 10px;
  padding: 15px 30px;
`

export const MessageText = styled.p`
  font-size: 16px;
`

export const ImagePreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`

export const ImagePreviewContent = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const ImagePreviewTitle = styled.h3`
  margin-bottom: 20px;
  color: #333;
`

export const ImagePreviewImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  margin-bottom: 20px;
`

export const ImagePreviewButtons = styled.div`
  display: flex;
  gap: 10px;
`

export const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  justify-content: center;
`

export const ImageContainer = styled.div`
  width: 100%;
  height: 70vh;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 20px;
  position: relative;
`

export const ImagePreviewImageZoom = styled.img<ImageZoomProps>`
  max-width: none;
  max-height: none;
  transform: scale(${(props) => props.zoom})
    translate(
      ${(props) => props.position.x / props.zoom}px,
      ${(props) => props.position.y / props.zoom}px
    );
  cursor: ${(props) =>
    props.zoom > 1 ? (props.isDragging ? 'grabbing' : 'grab') : 'default'};
  transition: ${(props) => (props.isDragging ? 'none' : 'transform 0.2s ease')};
  user-select: none;
  object-fit: contain;
`

export const ZoomControlsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  justify-content: center;
`

export const ZoomPercentage = styled.span`
  margin: 0 10px;
  font-size: 14px;
`

export const ImageViewerContainer = styled.div<ImageViewerContainerProps>`
  width: ${({ imageWidth, imageHeight }) => {
    if (!imageWidth || !imageHeight) return '100%'

    // Calculate the display size based on natural dimensions
    // but limit to viewport size
    const maxWidth = window.innerWidth * 0.8
    const maxHeight = window.innerHeight * 0.6

    const aspectRatio = imageWidth / imageHeight

    if (imageWidth <= maxWidth && imageHeight <= maxHeight) {
      return `${imageWidth}px`
    }

    if (aspectRatio > maxWidth / maxHeight) {
      return `${maxWidth}px`
    }

    return `${maxHeight * aspectRatio}px`
  }};
  height: ${({ imageWidth, imageHeight }) => {
    if (!imageWidth || !imageHeight) return '70vh'

    const maxWidth = window.innerWidth * 0.8
    const maxHeight = window.innerHeight * 0.6

    const aspectRatio = imageWidth / imageHeight

    if (imageWidth <= maxWidth && imageHeight <= maxHeight) {
      return `${imageHeight}px`
    }

    if (aspectRatio > maxWidth / maxHeight) {
      return `${maxWidth / aspectRatio}px`
    }

    return `${maxHeight}px`
  }};
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 20px;
  position: relative;
  min-width: 300px;
  min-height: 200px;
`

export const ZoomableImage = styled.img<ImageZoomProps>`
  width: ${({ naturalWidth, naturalHeight }) => {
    if (!naturalWidth || !naturalHeight) return 'auto'

    const maxWidth = window.innerWidth * 0.8
    const maxHeight = window.innerHeight * 0.6

    const aspectRatio = naturalWidth / naturalHeight

    if (naturalWidth <= maxWidth && naturalHeight <= maxHeight) {
      return `${naturalWidth}px`
    }

    if (aspectRatio > maxWidth / maxHeight) {
      return `${maxWidth}px`
    }

    return `${maxHeight * aspectRatio}px`
  }};
  height: ${({ naturalWidth, naturalHeight }) => {
    if (!naturalWidth || !naturalHeight) return 'auto'

    const maxWidth = window.innerWidth * 0.8
    const maxHeight = window.innerHeight * 0.6

    const aspectRatio = naturalWidth / naturalHeight

    if (naturalWidth <= maxWidth && naturalHeight <= maxHeight) {
      return `${naturalHeight}px`
    }

    if (aspectRatio > maxWidth / maxHeight) {
      return `${maxWidth / aspectRatio}px`
    }

    return `${maxHeight}px`
  }};
  max-width: none;
  max-height: none;
  transform: scale(${(props) => props.zoom})
    translate(
      ${(props) => props.position.x / props.zoom}px,
      ${(props) => props.position.y / props.zoom}px
    );
  cursor: ${(props) =>
    props.zoom > 1 ? (props.isDragging ? 'grabbing' : 'grab') : 'default'};
  transition: ${(props) => (props.isDragging ? 'none' : 'transform 0.2s ease')};
  user-select: none;
  object-fit: contain;
`

export const PdfViewerContainer = styled.div`
  width: 80vw;
  height: 70vh;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 20px;
  overflow: hidden;
  background-color: #f8f9fa;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  @media (max-width: 768px) {
    width: 95vw;
    height: 60vh;
  }
`

export const ScheduleButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 10px;

  button {
    transition: all 0.3s ease-in-out;
    background: transparent !important;
    border: 1px solid #ffa947 !important;
    color: #ffa947 !important;

    &:not(:disabled):hover {
      background: linear-gradient(135deg, #ff5100 0%, #f99036 100%) !important;
      color: #ffffff !important;
      border-color: transparent !important;
    }
  }

  .custom-tooltip {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    background: #2c2b43;
    color: #ffffff;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    line-height: 1.5;
    white-space: normal;
    width: 280px;
    text-align: center;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.3s ease-in-out,
      visibility 0.3s ease-in-out;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: #2c2b43;
    }
  }

  &.has-credits:hover .custom-tooltip {
    opacity: 1;
    visibility: visible;
  }
`
